import React, { useState, useEffect } from 'react';
import { FaTimes, FaChevronLeft, FaChevronRight, FaPlay, FaPause, FaMapMarkerAlt, FaPhone, FaEnvelope, FaEye, FaDownload } from 'react-icons/fa';
import { COMPANY_CONTACT } from '../config/company';
import { generateBillboardPDF } from '../utils/pdfGenerator';
import { getPricingDisplay, getPeriodRate, formatInstallFee } from '../utils/pricingDisplay';
import './InventoryModal.css';

const InventoryModal = ({ item, isOpen, onClose }) => {
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [videoRef, setVideoRef] = useState(null);

  // Combine images and videos into one media array
  const allMedia = [
    ...(item?.images || []).map(url => ({ type: 'image', url })),
    ...(item?.videos || []).map(url => ({ type: 'video', url }))
  ];

  useEffect(() => {
    if (isOpen) {
      setCurrentMediaIndex(0);
      setIsVideoPlaying(false);
    }
  }, [isOpen, item]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const nextMedia = () => {
    if (currentMediaIndex < allMedia.length - 1) {
      setCurrentMediaIndex(currentMediaIndex + 1);
      setIsVideoPlaying(false);
    }
  };

  const prevMedia = () => {
    if (currentMediaIndex > 0) {
      setCurrentMediaIndex(currentMediaIndex - 1);
      setIsVideoPlaying(false);
    }
  };

  const toggleVideoPlayback = () => {
    if (videoRef) {
      if (isVideoPlaying) {
        videoRef.pause();
      } else {
        videoRef.play();
      }
      setIsVideoPlaying(!isVideoPlaying);
    }
  };

  const formatTrafficCount = (count) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(0)}K`;
    }
    return count.toString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (!isOpen || !item) return null;

  const currentMedia = allMedia[currentMediaIndex];

  return (
    <div className="inventory-modal-overlay" onClick={onClose}>
      <div className="inventory-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>
          <FaTimes />
        </button>

        <div className="modal-content">
          {/* Media Gallery Section */}
          <div className="media-section">
            <div className="media-container">
              {currentMedia ? (
                currentMedia.type === 'image' ? (
                  <img 
                    src={currentMedia.url} 
                    alt={`${item.name} - Image ${currentMediaIndex + 1}`}
                    className="media-display"
                  />
                ) : (
                  <div className="video-container">
                    <video
                      ref={setVideoRef}
                      src={currentMedia.url}
                      className="media-display"
                      controls={false}
                      onPlay={() => setIsVideoPlaying(true)}
                      onPause={() => setIsVideoPlaying(false)}
                    />
                    <button 
                      className="video-play-btn"
                      onClick={toggleVideoPlayback}
                    >
                      {isVideoPlaying ? <FaPause /> : <FaPlay />}
                    </button>
                  </div>
                )
              ) : (
                <div className="no-media">
                  <p>No media available</p>
                </div>
              )}
            </div>

            {/* Media Navigation */}
            {allMedia.length > 1 && (
              <>
                <button 
                  className="media-nav-btn prev" 
                  onClick={prevMedia}
                  disabled={currentMediaIndex === 0}
                >
                  <FaChevronLeft />
                </button>
                <button 
                  className="media-nav-btn next" 
                  onClick={nextMedia}
                  disabled={currentMediaIndex === allMedia.length - 1}
                >
                  <FaChevronRight />
                </button>
              </>
            )}

            {/* Media Thumbnails */}
            {allMedia.length > 1 && (
              <div className="media-thumbnails">
                {allMedia.map((media, index) => (
                  <button
                    key={index}
                    className={`thumbnail ${index === currentMediaIndex ? 'active' : ''}`}
                    onClick={() => {
                      setCurrentMediaIndex(index);
                      setIsVideoPlaying(false);
                    }}
                  >
                    {media.type === 'image' ? (
                      <img src={media.url} alt={`Thumbnail ${index + 1}`} />
                    ) : (
                      <div className="video-thumbnail">
                        <FaPlay />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details Section */}
          <div className="details-section">
            <div className="item-header">
              <h2>{item.name}</h2>
              <p className="sign-number">Sign #{item.signNumber}</p>
              <div className="location-info">
                <FaMapMarkerAlt />
                <span>{item.city}</span>
              </div>
              <div className="traffic-info">
                <strong>{formatTrafficCount(item.weeklyTrafficCount)}</strong> weekly traffic
              </div>
            </div>

            <div className="details-content">
              {item.description && (
                <div className="detail-section">
                  <h4>Description</h4>
                  <p className="description-text">{item.description}</p>
                </div>
              )}
              <div className="detail-section">
                <h4>Location Details</h4>
                <div className="detail-row">
                  <span>Coordinates:</span>
                  <span>{item.latitude}, {item.longitude}</span>
                </div>
                <div className="detail-row">
                  <span>City:</span>
                  <span>{item.city}</span>
                </div>
              </div>

              <div className="detail-section">
                <h4>Specifications</h4>
                <div className="detail-row">
                  <span>Panel Size:</span>
                  <span>{item.panelSize}</span>
                </div>
                <div className="detail-row">
                  <span>Vinyl Size:</span>
                  <span>{item.vinylSize}</span>
                </div>
                {item.illuminated != null && (
                  <div className="detail-row">
                    <span>Illuminated:</span>
                    <span>{item.illuminated ? 'Yes' : 'No'}</span>
                  </div>
                )}
                {item.billingPeriod === '4week' && (
                  <div className="detail-row">
                    <span>Billing Period:</span>
                    <span>4-week periods</span>
                  </div>
                )}
              </div>

              <div className="detail-section">
                <h4>Pricing & Availability</h4>
                <div className="pricing-tables-container">
                  {item.pricing.directions.map((direction, index) => {
                    const pricingDisplay = getPricingDisplay(
                      item.billingPeriod,
                      item.excludePricingTiers
                    );
                    return (
                    <div key={index} className="pricing-table-wrapper">
                      <h5 className="direction-title">{direction.direction}</h5>
                      <div className="pricing-table-container">
                        <table className="pricing-table">
                          <thead>
                            <tr>
                              <th>Contract</th>
                              <th>{pricingDisplay.perPeriodLabel}</th>
                              <th>Vinyl & Install</th>
                              <th>{pricingDisplay.firstPeriodLabel}</th>
                              <th>{pricingDisplay.secondPeriodLabel}</th>
                              <th>{pricingDisplay.ongoingLabel}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pricingDisplay.tiers.map((tier) => {
                              const tierData = direction[tier.key];
                              const rate = getPeriodRate(tierData);
                              return (
                                <tr key={tier.key}>
                                  <td><strong>{tier.label}</strong></td>
                                  <td>{formatCurrency(rate)}</td>
                                  <td>{formatInstallFee(tierData.vinylInstall)}</td>
                                  <td>{formatCurrency(rate + tierData.vinylInstall)}</td>
                                  <td>{formatCurrency(rate)}</td>
                                  <td>{formatCurrency(rate)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>

              <div className="contact-actions">
              <a href={`tel:${COMPANY_CONTACT.phoneLink}`} className="contact-btn phone call-now-btn">
                <FaPhone /> Call Now
              </a>
              <a href={`mailto:${COMPANY_CONTACT.email}`} className="contact-btn email">
                <FaEnvelope /> Email Inquiry
              </a>
              <a 
                href={`https://www.google.com/maps/search/?api=1&query=${item.latitude},${item.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                className="contact-btn view"
              >
                <FaEye /> View Location
              </a>
              <button 
                onClick={() => {
                  console.log('Download PDF button clicked for:', item.name);
                  generateBillboardPDF(item);
                }}
                className="contact-btn download"
              >
                <FaDownload /> Download PDF
              </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryModal; 